import { useCallback, useMemo, useState } from "react";
import { Text, DropdownButton, Flex, toast, QueryLoader, AgGrid } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { globalEnv } from "~/global-env";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { useCreateComplianceCaseFromProfileMutation } from "~/graphql/supergraphHooks";
import { BaseRequestModal } from "../BaseRequestModal";
import { useProfileContacts } from "../hooks/useProfileContacts";
import { CancelDocumentRequestModal } from "./CancelDocumentRequestModal";
import { MENU_OPTIONS_BY_STATUS, MENU_ACTION_KEYS } from "./constants";
import { dropdownContainer, menuActionContent } from "./styles_vanilla.css";
import { DocumentRefreshStatus, DocumentRefreshCellRendererProps } from "./types";

export type ActionsMenuProps = {
  status: DocumentRefreshStatus;
  data: DocumentRefreshCellRendererProps["data"];
  api?: AgGrid.GridApi;
};

export const ActionsMenu = ({ status, data, api }: ActionsMenuProps) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    isResend: false,
  });
  const [showCancelModal, setShowCancelModal] = useState(false);

  const queriesEnabled = modalState.isOpen || showCancelModal;
  const { emails, profileQuery, contactsQuery } = useProfileContacts(data?.id, queriesEnabled);

  const [createComplianceCaseFromProfile] = useCreateComplianceCaseFromProfileMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      api?.refreshServerSide({ purge: true });
      toast.success("Case created successfully");
    },
  });

  const handleOpenModal = useCallback((isResend: boolean) => {
    setModalState({ isOpen: true, isResend });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, isResend: false });
  }, []);

  const handleSendDocumentRequest = useCallback(() => {
    handleOpenModal(false);
  }, [handleOpenModal]);

  const handleResendDocumentRequest = useCallback(() => {
    handleOpenModal(true);
  }, [handleOpenModal]);

  const handleCancelDocumentRequest = useCallback(() => {
    if (!data?.documentInvitationId) {
      toast.error("Invitation ID is missing");
      return;
    }
    setShowCancelModal(true);
  }, [data?.documentInvitationId]);

  const handleCloseCancelModal = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handleCreateNewCase = useCallback(async () => {
    if (!data?.profileGuid) {
      toast.error("Profile GUID is missing");
      return;
    }

    await createComplianceCaseFromProfile({
      variables: {
        caseInput: {
          profileGuid: data.profileGuid,
          caseType: ComplianceCaseTypeEnum.DocumentRefresh,
          assignSelf: false,
        },
        emailInviteInput: null,
      },
    });
  }, [data, createComplianceCaseFromProfile]);

  const handleViewCase = useCallback(() => {
    if (data?.documentRefreshCaseId) {
      window.open(
        `/canopy${globalEnv.complianceRoute}/case/${data.documentRefreshCaseId}`,
        "_blank",
      );
    }
  }, [data?.documentRefreshCaseId]);

  const menuOptions = useMemo(() => {
    const handlers: Record<string, () => void> = {
      [MENU_ACTION_KEYS.VIEW_CASE]: handleViewCase,
      [MENU_ACTION_KEYS.SEND_DOCUMENT_UPDATE_REQUEST]: handleSendDocumentRequest,
      [MENU_ACTION_KEYS.RESEND_DOCUMENT_UPDATE_REQUEST]: handleResendDocumentRequest,
      [MENU_ACTION_KEYS.CANCEL_DOCUMENT_UPDATE_REQUEST]: handleCancelDocumentRequest,
      [MENU_ACTION_KEYS.CREATE_CASE]: handleCreateNewCase,
    };

    const baseOptions = MENU_OPTIONS_BY_STATUS[status] ?? [];

    return baseOptions.map((option) => ({
      ...option,
      onSelect: handlers[option.label],
    }));
  }, [
    status,
    handleViewCase,
    handleSendDocumentRequest,
    handleResendDocumentRequest,
    handleCancelDocumentRequest,
    handleCreateNewCase,
  ]);

  if (menuOptions.length === 0) {
    return null;
  }

  return (
    <>
      <div className={dropdownContainer}>
        <DropdownButton
          variant="muted"
          icon="ellipsis-h"
          direction="down"
          align="right"
          usePortal
        >
          {menuOptions.map((option) => (
            <DropdownButton.Action key={option.label} onSelect={option.onSelect}>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                className={menuActionContent}
              >
                <Text>{option.label}</Text>
                {option.iconType && <Icon type={option.iconType} />}
              </Flex>
            </DropdownButton.Action>
          ))}
        </DropdownButton>
      </div>
      {modalState.isOpen && (
        <QueryLoader result={[profileQuery, contactsQuery]}>
          <BaseRequestModal
            requestType="document_refresh"
            profileId={data?.id}
            profileName={data?.name}
            profileGuid={data?.profileGuid}
            caseId={data?.documentRefreshCaseId}
            isResend={modalState.isResend}
            dateSent={data?.documentRefreshTooltipData?.requestSentDate}
            requestSentCount={data?.documentRefreshTooltipData?.requestSentCount}
            emails={emails}
            onClose={handleCloseModal}
            api={api}
          />
        </QueryLoader>
      )}
      {showCancelModal && data?.documentInvitationId && (
        <QueryLoader result={[profileQuery, contactsQuery]}>
          <CancelDocumentRequestModal
            show={showCancelModal}
            onHide={handleCloseCancelModal}
            profileName={data?.name || ""}
            recipients={emails}
            invitationId={data.documentInvitationId}
            api={api}
          />
        </QueryLoader>
      )}
    </>
  );
};
